

import { Injectable } from '@angular/core';
import { EventFilterType } from '../../event/models/event-filter-type-enum';
import { EventSearchFilter } from '../../event/models/event-filter';
import { OrderedSet } from 'immutable';
import { EventMenuSearchFilter } from '../../event/models/event-menu-search-filter';
import { EventFilterUtilsService } from '../../event/service/event-filter-util-service';
import { DateUtil } from '../../shared/utils/date.util';

@Injectable()
export class EventMenuSearchFilterMapper {


  toSearchFilterSet(filterRecord: EventMenuSearchFilter): OrderedSet<EventSearchFilter> {

    let filterSet: OrderedSet<EventSearchFilter> = OrderedSet([]);

    if (filterRecord.commissionId && filterRecord.commissionId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.COMMISSION_ID, filterRecord.commissionId) );
    }

    if (filterRecord.fundingReferenceId && filterRecord.fundingReferenceId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.FUNDING_REFERENCE_ID,
        filterRecord.fundingReferenceId) );
    }

    if (filterRecord.paymentReferenceId && filterRecord.paymentReferenceId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_REFERENCE_ID,
        filterRecord.paymentReferenceId));
    }

    if ((filterRecord.endDate && filterRecord.endDate !== '') && (filterRecord.startDate && filterRecord.startDate !== '')) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_RANGE,
        EventFilterUtilsService.getDateRangeFilterValue(filterRecord.startDate, filterRecord.endDate)));
    } else if (filterRecord.startDate && filterRecord.startDate !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_AFTER,
        DateUtil.formatDate(filterRecord.startDate)));
    } else if (filterRecord.endDate && filterRecord.endDate !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_BEFORE,
        DateUtil.formatDate(filterRecord.endDate)));
    }

    if (filterRecord.eventStatusCompleted) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.COMPLETED,
        JSON.stringify(filterRecord.eventStatusCompleted)));
    }

    if (filterRecord.eventStatusMissingAgency) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.MISSING_AGENCY,
        JSON.stringify(filterRecord.eventStatusMissingAgency)));
    }

    if (filterRecord.guestName) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.GUEST_NAME, filterRecord.guestName));
    }

    filterSet = this.buildPaymentFilters(filterRecord, filterSet);

    return filterSet;
  }

  private asSearchFilter(filterField: EventFilterType, filterValue: string): EventSearchFilter {
    const filter = new EventSearchFilter({
      filterField: filterField.toString(),
      filterValue: filterValue ? filterValue.trim() : filterValue
    });

    return filter;
  }

  private buildPaymentFilters(filterRecord: EventMenuSearchFilter,
                              filterSet: OrderedSet<EventSearchFilter>): OrderedSet<EventSearchFilter> {

    const paymentStatusRecord = filterRecord.paymentStatus;

    const allPaymentFiltersSelected =
      paymentStatusRecord.unpaid &&
      paymentStatusRecord.paid &&
      paymentStatusRecord.inProcess;

    // no need to filter if all 3 are selected
    if (allPaymentFiltersSelected) {
      return filterSet;
    }

    // reapply selected filters
    if (paymentStatusRecord.inProcess) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'paymentInProcess'));
    }

    if (paymentStatusRecord.paid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'paid'));
    }

    if (paymentStatusRecord.unpaid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'unpaid'));
    }

    return filterSet;
  }

  toEventMenuSearchFilter(filterSet: OrderedSet<EventSearchFilter>): EventMenuSearchFilter {
    let filterRecord: any = {};

    filterRecord.eventStatusMissingAgency = this.hasSearchFilter(filterSet, EventFilterType.MISSING_AGENCY);
    filterRecord.eventStatusCompleted = this.hasSearchFilter(filterSet, EventFilterType.COMPLETED);
    filterRecord.guestName = this.getSearchFilterValue(filterSet, EventFilterType.GUEST_NAME);
    filterRecord.commissionId = this.getSearchFilterValue(filterSet, EventFilterType.COMMISSION_ID);
    filterRecord.paymentReferenceId = this.getSearchFilterValue(filterSet, EventFilterType.PAYMENT_REFERENCE_ID);
    filterRecord.fundingReferenceId = this.getSearchFilterValue(filterSet, EventFilterType.FUNDING_REFERENCE_ID);
    filterRecord.startDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_AFTER);
    filterRecord.endDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_BEFORE);

    if (this.hasSearchFilter(filterSet, EventFilterType.DATE_RANGE)) {
      const dates: string[] = EventFilterUtilsService.parseFilterValueAsDateRange(
        this.getSearchFilterValue(filterSet,
          EventFilterType.DATE_RANGE));

      filterRecord.startDate = dates[0];
      filterRecord.endDate = dates[1];
    }

    let paymentStatusRecord: any = {};


    paymentStatusRecord.unpaid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'unpaid');

    paymentStatusRecord.inProcess =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'paymentInProcess');

    paymentStatusRecord.paid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'paid');

    const allPaymentStatusFiltersAreFalse =
      !paymentStatusRecord.unpaid &&
      !paymentStatusRecord.inProcess &&
      !paymentStatusRecord.paid;

    if (allPaymentStatusFiltersAreFalse) {
      // then they they all need to be enabled
      paymentStatusRecord.unpaid = true;
      paymentStatusRecord.inProcess = true;
      paymentStatusRecord.paid = true;
    }

    filterRecord.paymentStatus = paymentStatusRecord;



    let updatedRecord: EventMenuSearchFilter = new EventMenuSearchFilter(filterRecord);

    return updatedRecord;
  }

  private hasSearchFilter(filterSet: OrderedSet<EventSearchFilter>, filterField: EventFilterType): boolean {
    let isEmpty = filterSet.filter(filter => {
      return filterField.toString() === filter.filterField;
    }).isEmpty();
    return !isEmpty;
  }

  private getSearchFilterValue(filterSet: OrderedSet<EventSearchFilter>, filterField: EventFilterType): string {
    return filterSet.filter( (filter: EventSearchFilter) => {
      return filterField.toString() === filter.filterField;
    }).map((filter: EventSearchFilter) => filter.filterValue ).first();
  }

  private hasSearchFilterByFieldAndValue(filterSet: OrderedSet<EventSearchFilter>,
                                         filterField: EventFilterType,
                                         filterValue: string): boolean {
    let isEmpty = filterSet.filter(filter => {
      return filterField.toString() === filter.filterField && filterValue === filter.filterValue;
    }).isEmpty();
    return !isEmpty;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/menus/event-search-filter/event-menu-search-filter-mapper.ts